import { useEffect, useState } from 'react';

import type { ProgressStep } from './StepLayout/consts';
import { STEP_KEY, STEP_NAME, TRANSFER_INTENT_PROGRESS_STEP } from './StepLayout/consts';
import { getOnboardingStorage } from './util';

interface SetInitialOnboardingProps {
  onHydrateStoredFields(storedFields: any): void;
  onValidateEmail(email: string): void;
  clearOnboardingStorage(): void;
  onSetInitialOnboardingStep(step: STEP_NAME): void;
  zipCodeParam: string;
  isDeliverableParam: string;
  onSetOnboardingFormValues(values: { [key: string]: string | boolean }): void;
  onSetCurrentOnboardingStep(step: STEP_NAME): void;
  onAddStepAfterKey(stepKey: STEP_KEY, progressStep: ProgressStep): void;
  currentOnboardingStep: string;
}

/**
 * Determines initial onboarding step entry point based on deliverability, lead returns, and experiments and sets
 * initial step
 * @returns isLoading state of determining initial step
 */
export const useSetInitialOnboardingStepAndParams = ({
  onHydrateStoredFields,
  onValidateEmail,
  clearOnboardingStorage,
  onSetInitialOnboardingStep,
  zipCodeParam,
  isDeliverableParam,
  onSetOnboardingFormValues,
  onSetCurrentOnboardingStep,
  onAddStepAfterKey,
}: SetInitialOnboardingProps) => {
  const [isLoadingEntry, setIsLoadingEntry] = useState(true);
  const [hasSetInitialSteps, setHasSetInitialSteps] = useState(false);

  const loadAndValidateStorage = async () => {
    const storedFields = getOnboardingStorage();
    if (storedFields) {
      onHydrateStoredFields(storedFields);
      // Check if email is valid before welcome back step, SMS can go directly to welcome back
      if (storedFields?.email) {
        try {
          await onValidateEmail(storedFields?.email);
        } catch (e) {
          clearOnboardingStorage();
          onSetInitialOnboardingStep(STEP_NAME.WELCOME);
        }
      }
    }
  };

  const redirectToWelcomeBack = () => {
    onSetCurrentOnboardingStep(STEP_NAME.TRANSFER_INTENT);
  };

  const hasZipCodeParams = zipCodeParam && isDeliverableParam;
  const isDeliverableParamBoolean = isDeliverableParam === 'true';

  useEffect(() => {
    const setInitialOnboardingStepAndParams = async () => {
      setHasSetInitialSteps(true);
      const hasStoredFields = getOnboardingStorage();
      await loadAndValidateStorage();

      onAddStepAfterKey(STEP_KEY.CONTACT, TRANSFER_INTENT_PROGRESS_STEP);

      if (hasZipCodeParams) {
        await onSetOnboardingFormValues({
          zipCode: zipCodeParam,
          isZipcodeDeliverable: isDeliverableParamBoolean,
        });

        if (isDeliverableParamBoolean && hasStoredFields) {
          redirectToWelcomeBack();
        } else if (isDeliverableParamBoolean && !hasStoredFields) {
          onSetCurrentOnboardingStep(STEP_NAME.ELIGIBILITY_CONFIRMATION);
        } else if (!isDeliverableParamBoolean) {
          onSetCurrentOnboardingStep(STEP_NAME.JOIN_WAITLIST);
        }
      } else if (hasStoredFields) {
        redirectToWelcomeBack();
      }
      setIsLoadingEntry(false);
    };

    if (!hasSetInitialSteps) {
      setInitialOnboardingStepAndParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isLoadingEntry;
};
